import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { FaRobot, FaSave, FaCheckCircle, FaVial } from 'react-icons/fa';

export default function AdminAIConfig() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  
  // AI Settings State
  const [autoModeration, setAutoModeration] = useState(true);
  const [modSensitivity, setModSensitivity] = useState(0.7); // 0.0 to 1.0
  const [aiAssistantEnabled, setAiAssistantEnabled] = useState(true);
  const [aiPersona, setAiPersona] = useState('You are a helpful, expert bass fishing guide. Keep responses concise, practical, and focused on bass fishing, boat maintenance, and local lakes. Never pretend to be a human.');
  const [autoSummarizeThreads, setAutoSummarizeThreads] = useState(true);
  
  // Test Moderation State
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testingMod, setTestingMod] = useState(false);

  // In a real app, we would fetch current config from backend
  // For this V1, we simulate fetching config
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccessMsg('');
    
    // Simulate API save
    setTimeout(() => {
      setSaving(false);
      setSuccessMsg('AI Configuration saved successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
    }, 1000);
  };

  const handleTestModeration = async () => {
    if (!testText.trim()) return;
    
    setTestingMod(true);
    setTestResult(null);
    
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/test-moderation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: testText })
      });
      const data = await res.json();
      if (data.success) {
        setTestResult(data.result);
      }
    } catch (err) {
      console.error(err);
      setTestResult({ flagged: false, error: 'Failed to run test' });
    } finally {
      setTestingMod(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  return (
    <div className="py-4 community-admin-container">
      <div className="mb-4">
        <h2><FaRobot className="me-2 text-primary" /> AI Configuration</h2>
        <p className="text-muted">Manage the behavior, persona, and strictness of the AI systems powering the community.</p>
      </div>

      {successMsg && <Alert variant="success"><FaCheckCircle className="me-2" />{successMsg}</Alert>}

      <Row>
        <Col lg={8}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white fw-bold py-3">Global AI Settings</Card.Header>
            <Card.Body>
              <Form onSubmit={handleSaveConfig}>
                <h5 className="mb-3 text-primary">Content Moderation (OpenAI)</h5>
                
                <Form.Group className="mb-4">
                  <Form.Check 
                    type="switch"
                    id="auto-mod-switch"
                    label={<span className="fw-bold">Enable Auto-Moderation</span>}
                    checked={autoModeration}
                    onChange={(e) => setAutoModeration(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    If enabled, all new threads and replies will be scanned by OpenAI's Moderation API before being published. 
                    Flagged content is sent to the Admin Moderation Queue.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Moderation Sensitivity Threshold: {modSensitivity}</Form.Label>
                  <Form.Range 
                    min="0.1" 
                    max="0.9" 
                    step="0.1" 
                    value={modSensitivity}
                    onChange={(e) => setModSensitivity(parseFloat(e.target.value))}
                    disabled={!autoModeration}
                  />
                  <div className="d-flex justify-content-between small text-muted">
                    <span>Strict (Flags easily)</span>
                    <span>Lenient (Flags less)</span>
                  </div>
                </Form.Group>

                <hr className="my-4" />

                <h5 className="mb-3 text-primary">AI Assistant Persona</h5>

                <Form.Group className="mb-4">
                  <Form.Check 
                    type="switch"
                    id="ai-assistant-switch"
                    label={<span className="fw-bold">Enable Interactive AI Assistant</span>}
                    checked={aiAssistantEnabled}
                    onChange={(e) => setAiAssistantEnabled(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    Allows users to specifically ask the AI for advice in threads. AI replies will be badged as "BBR AI Assistant".
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">System Prompt (Persona Instruction)</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={4} 
                    value={aiPersona}
                    onChange={(e) => setAiPersona(e.target.value)}
                    disabled={!aiAssistantEnabled}
                  />
                  <Form.Text className="text-muted">
                    This is the hidden prompt injected into every request sent to the AI Assistant. It dictates the AI's tone and rules.
                  </Form.Text>
                </Form.Group>

                <hr className="my-4" />

                <h5 className="mb-3 text-primary">Content Enhancement</h5>

                <Form.Group className="mb-4">
                  <Form.Check 
                    type="switch"
                    id="auto-summarize-switch"
                    label={<span className="fw-bold">Auto-summarize long threads</span>}
                    checked={autoSummarizeThreads}
                    onChange={(e) => setAutoSummarizeThreads(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    Automatically generates a "TL;DR" summary for threads with more than 10 replies.
                  </Form.Text>
                </Form.Group>

                <div className="text-end">
                  <Button variant="primary" type="submit" disabled={saving}>
                    {saving ? <Spinner size="sm" className="me-2" /> : <FaSave className="me-2" />}
                    Save Configuration
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card className="shadow-sm border-0 bg-light">
            <Card.Header className="bg-light fw-bold py-3"><FaVial className="me-2 text-warning"/> Test Moderation API</Card.Header>
            <Card.Body>
              <p className="small text-muted mb-3">
                Test how the current OpenAI moderation model handles specific phrases.
              </p>
              <Form.Group className="mb-3">
                <Form.Control 
                  as="textarea" 
                  rows={3}
                  placeholder="Enter text to test..."
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                />
              </Form.Group>
              <Button variant="outline-primary" size="sm" className="w-100 mb-3" onClick={handleTestModeration} disabled={testingMod || !testText}>
                {testingMod ? <Spinner size="sm" /> : 'Run Test'}
              </Button>
              
              {testResult && (
                <div className="mt-3 p-3 bg-white border rounded">
                  <h6 className="mb-2 border-bottom pb-1">Result</h6>
                  {testResult.flagged ? (
                    <>
                      <Badge bg="danger" className="mb-2">FLAGGED</Badge>
                      <ul className="small text-danger ps-3 mb-0">
                        {Object.entries(testResult.categories || {})
                          .filter(([_, value]) => value)
                          .map(([key]) => (
                            <li key={key}>{key}</li>
                          ))
                        }
                      </ul>
                    </>
                  ) : (
                    <Badge bg="success">SAFE (Not Flagged)</Badge>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
